import { create } from 'zustand';
import { Capacitor } from '@capacitor/core';
import { createFlow, onSessionChange } from './session-read';

export interface NotificationRouteState {
  pendingRoute: string | null;
  /**
   * The pending route a sign-out dropped (a cold-start tap whose `/me` came
   * back 401): the sign-in it now needs carries it as `?redirect=`
   * (ProtectedRoute), so the push still lands where it said. Cleared by the
   * next sign-in, which has read it from the login URL by then. Until then it
   * only names the bounce from where a cold start sits (`signInDestination`):
   * a later link opened while still signed out keeps its own (c-080).
   */
  signedOutRoute: string | null;
  isResolving: boolean;
  setPendingRoute: (route: string) => void;
  consumePendingRoute: () => string | null;
  markResolved: () => void;
}

/**
 * A pending deep link is a flow owned by the session it arrived under
 * (session-read.ts createFlow): a push for A, consumed after B signed in,
 * would send B to A's target (#940).
 */
const pending = createFlow();

export const useNotificationRouteStore = create<NotificationRouteState>((set, get) => ({
  pendingRoute: null,
  signedOutRoute: null,
  isResolving: Capacitor.isNativePlatform(),
  setPendingRoute: (route) => {
    pending.open();
    set({ pendingRoute: route, isResolving: false });
  },
  consumePendingRoute: () => {
    const route = get().pendingRoute;
    const owned = pending.owns(pending.current());
    if (route) set({ pendingRoute: null });
    pending.close();
    return owned ? route : null;
  },
  markResolved: () => set({ isResolving: false }),
}));

// A session change drops the previous session's pending deep link: it must
// never send ANOTHER account to it (#940). A sign-out (the token gone, as a
// 401 on `/me` does) keeps it only as the login's `?redirect=`, for the same
// person signing back in on this device; any sign-in clears that too.
onSessionChange((next) => {
  const carried = next === null ? useNotificationRouteStore.getState().pendingRoute : null;
  pending.close();
  useNotificationRouteStore.setState({ pendingRoute: null, signedOutRoute: carried });
});

/**
 * Where a sign-in should continue to: a pushed destination still waiting (a
 * failed `/me` kept the token) or one a sign-out carried, else null.
 */
export function notificationLoginRedirect(state: NotificationRouteState): string | null {
  return state.pendingRoute ?? state.signedOutRoute;
}

/** Where a native cold start sits before any route of its own: `/` or bare `/app`. */
const COLD_START_SEATS = new Set(['/', '/app', '/app/']);

/**
 * What a login bounce from `here` should continue to. A pushed route (see
 * notificationLoginRedirect) wins only on the page a cold start sits on, the
 * `/app` it is not headed for (NOTIFICATION-TAP-ROUTES decision 5). Anywhere
 * else the page is the user's own destination: a shared link opened while a
 * stale pushed route waited for a sign-in was sent to the push (backlog c-080).
 */
export function signInDestination(
  pushedRoute: string | null,
  here: { pathname: string; search: string; hash: string },
): string {
  if (pushedRoute && COLD_START_SEATS.has(here.pathname)) return pushedRoute;
  return `${here.pathname}${here.search}${here.hash}`;
}
